import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Text,
  Button,
  IconButton,
  Spacer,
  HStack,
  Flex,
  Spinner,
  useDisclosure,
  Checkbox,
} from '@chakra-ui/react';
import {
  ApprovalCardSimplified,
  ChevronLeftOutline,
  CloseCircleOutline,
  CodeDescription,
  getError,
  Individual,
  individualFullName,
  TickCircleOutline,
  useToast,
  YellowWarningBold,
} from '@aslanpay/portal-common-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useApproveSelectedRecognitions from 'app/hooks/useApproveSelectedRecognitions';
import useRecognition from 'app/hooks/useRecognition';
import usePots from 'app/hooks/usePots';
import ApproveRecognitionModal from 'app/components/Modals/RecognitionApprovalsModals/RecognitionApprovalModal/RecognitionApprovalModal';
import RejectRecognitionModal from 'app/components/Modals/RecognitionApprovalsModals/RejectRecognitionModal/RejectRecognitionModal';
import TransferPointsForm from 'app/components/Modals/TransferPointsModal/TransferPointsForm/TransferPointsForm';
import { RecognitionWithPot } from 'app/pages/RewardAndRecognition/Approvals/Approvals.types';

import formatNumber from 'app/utils/formatting';

import BulkActionBar from 'app/components/BulkActionBar/BulkActionBar';

import getPointsValue from 'app/utils/points';

import { ArchivePotModalProps } from './ArchivePotModal.types';
import ArchivePotMainView from './ArchivePotMainView';

type ArchivePotModalView = 'main' | 'pendingApprovals' | 'transferPoints';

const POT_HAS_BALANCE: CodeDescription = 'POT_HAS_BALANCE';
const PENDING_APPROVALS: CodeDescription = 'PENDING_APPROVALS';

const isProcessing = (item: RecognitionWithPot) => item.status === 'APPROVING';

function ArchivePotModal({
  isOpen,
  onClose,
  companyId,
  originGroupWithPot,
  onSuccess,
}: ArchivePotModalProps) {
  const toast = useToast();

  const { isOpen: isApproveOpen, onOpen: openApprove, onClose: closeApprove } = useDisclosure();
  const { isOpen: isRejectOpen, onOpen: openReject, onClose: closeReject } = useDisclosure();

  // Declined ids are hidden straight away rather than waiting for the list to refresh.
  const [declinedIds, setDeclinedIds] = useState<Set<string>>(new Set());

  const [view, setView] = useState<ArchivePotModalView>('main');
  const [selected, setSelected] = useState<RecognitionWithPot[]>([]);
  const [pendingItems, setPendingItems] = useState<RecognitionWithPot[]>([]);

  const potName = originGroupWithPot?.pot.name || originGroupWithPot?.name || '';
  const originPotId = originGroupWithPot?.pot.id;

  const { approve, isApproving } = useApproveSelectedRecognitions();
  const { mutateAsync: rejectRecognition } = useRecognition.useRejectRecognition();

  const { mutate: archivePotMutation, isPending: isArchiving } = usePots.useArchivePot();
  const archivePot = useCallback(
    (...args: Parameters<typeof archivePotMutation>) => {
      if (isArchiving) return;
      archivePotMutation(...args);
    },
    [archivePotMutation, isArchiving]
  );

  const { data: originPotData } = usePots.useFetchPots(
    { ids: originPotId ? [originPotId] : [], page: 0, size: 1 },
    !!originPotId && isOpen
  );
  const currentBalance = originPotData?.list?.[0]?.balance ?? originGroupWithPot?.pot?.balance ?? 0;

  const liveOriginGroupWithPot = useMemo(() => {
    if (!originGroupWithPot) return originGroupWithPot;
    const livePot = originPotData?.list?.[0];
    if (!livePot) return originGroupWithPot;
    return { ...originGroupWithPot, pot: livePot };
  }, [originGroupWithPot, originPotData]);

  const {
    data: requests,
    isLoading: isLoadingPendingApprovals,
    refetch,
  } = useRecognition.useListPendingApprovals(
    {
      page: 0,
      size: 9999,
      sortBy: 'submittedAt',
      sort: 'ASC',
      // By the pot the points come from: a pot id is not a group id, and pending-approvals ignores
      // groupIds for pot managers.
      sourcePotIds: originGroupWithPot?.pot ? [originGroupWithPot.pot.id] : undefined,
    },
    isOpen
  );

  const pendingApprovals: RecognitionWithPot[] = useMemo(
    () =>
      (requests?.list ?? [])
        .map((item) => ({
          ...item,
          groupWithPot: originGroupWithPot,
          potManagers: [],
        }))
        .filter((item) => !declinedIds.has(item.id)),
    [requests, declinedIds, originGroupWithPot]
  );

  const selectableApprovals = useMemo(
    () => pendingApprovals.filter((item) => !isProcessing(item)),
    [pendingApprovals]
  );

  const hasPendingApprovals = pendingApprovals.length > 0;
  const hasBalance = currentBalance > 0;

  const isAllSelected =
    selectableApprovals.length > 0 &&
    selectableApprovals.every((m) => selected.some((s) => s.id === m.id));

  const isAllSelectedIndeterminate =
    selectableApprovals.length > 0 &&
    selectableApprovals.some((m) => selected.some((s) => s.id === m.id)) &&
    !isAllSelected;

  const onToggleSelectAll = useCallback(() => {
    setSelected(isAllSelected ? [] : selectableApprovals);
  }, [isAllSelected, selectableApprovals]);

  const handleOnSelect = useCallback(
    (id: string) => {
      setSelected((prev) => {
        if (prev.some((item) => item.id === id)) return prev.filter((item) => item.id !== id);
        const found = selectableApprovals.find((item) => item.id === id);
        return found ? [...prev, found] : prev;
      });
    },
    [selectableApprovals]
  );

  const openApproveConfirm = useCallback(
    (items: RecognitionWithPot[]) => {
      setPendingItems(items);
      openApprove();
    },
    [openApprove]
  );

  const openRejectConfirm = useCallback(
    (items: RecognitionWithPot[]) => {
      setPendingItems(items);
      openReject();
    },
    [openReject]
  );

  const handleConfirmApprove = useCallback(async () => {
    const canClose = await approve(pendingItems.map((item) => item.id));

    if (canClose) {
      setSelected((prev) => prev.filter((s) => !pendingItems.some((p) => p.id === s.id)));
      closeApprove();
      setPendingItems([]);
    }
  }, [pendingItems, approve, closeApprove]);

  const handleConfirmReject = useCallback(
    async (reason: string) => {
      try {
        await Promise.all(
          pendingItems.map((item) =>
            rejectRecognition({ recognitionId: item.id, body: { reason } })
          )
        );
        setDeclinedIds((prev) => new Set([...prev, ...pendingItems.map((i) => i.id)]));
        setSelected((prev) => prev.filter((s) => !pendingItems.some((p) => p.id === s.id)));
        closeReject();
        setPendingItems([]);
      } catch {
        toast({
          position: 'top-right',
          status: 'error',
          title:
            pendingItems.length > 1
              ? `Failed to decline ${pendingItems.length} entries`
              : 'Failed to decline recognition',
        });
      }
    },
    [pendingItems, rejectRecognition, closeReject, toast]
  );

  const handleClose = () => {
    setView('main');
    setSelected([]);
    setDeclinedIds(new Set());
    onClose();
  };

  const handleSuccess = () => {
    handleClose();
    onSuccess?.();
  };

  const handleArchivePot = () => {
    if (!originGroupWithPot?.pot?.id) return;

    archivePot(originGroupWithPot.pot.id, {
      onSuccess: () => {
        toast({
          position: 'top-right',
          title: `${potName} has been archived`,
          status: 'success',
        });
        handleSuccess();
      },
      onError: (error) => {
        const { codeDescription } = getError(error);

        if (codeDescription === POT_HAS_BALANCE) {
          toast({
            position: 'top-right',
            status: 'error',
            title: `Failed to archive ${potName}`,
            description:
              'This pot still has a balance. Transfer the remaining points before archiving.',
          });
          return;
        }

        if (codeDescription === PENDING_APPROVALS) {
          toast({
            position: 'top-right',
            status: 'error',
            title: `Failed to archive ${potName}`,
            description:
              'This pot has pending approvals. Resolve all pending recognitions before archiving.',
          });
          refetch();
          return;
        }

        toast({
          position: 'top-right',
          title: `Failed to archive ${potName}`,
          status: 'error',
        });
      },
    });
  };

  useEffect(() => {
    if (view === 'pendingApprovals' && !hasPendingApprovals) {
      setView('main');
    }
  }, [hasPendingApprovals, view]);

  const renderHeader = () => {
    if (view === 'pendingApprovals') {
      return (
        <Flex direction="column" gap="20px">
          <HStack align="flex-start">
            <Flex direction="row" align="center" gap={2}>
              <IconButton
                variant="link"
                icon={<ChevronLeftOutline boxSize="24px" />}
                aria-label="Back"
                onClick={() => setView('main')}
                color="brand.purple"
              />
              <Text as="h4" textStyle="h4Bold" color="brand.black">
                Pending approvals for {potName ? `"${potName}"` : 'this pot'}
              </Text>
            </Flex>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close modal"
              onClick={handleClose}
            />
          </HStack>

          <Checkbox
            ml="20px"
            isIndeterminate={isAllSelectedIndeterminate}
            isChecked={isAllSelected}
            isDisabled={selectableApprovals.length === 0}
            onChange={onToggleSelectAll}
            variant="selectAll"
          >
            Select all
          </Checkbox>
        </Flex>
      );
    }

    if (view === 'transferPoints') {
      return (
        <HStack align="flex-start">
          <Flex direction="row" align="center" gap={2}>
            <IconButton
              variant="link"
              icon={<ChevronLeftOutline boxSize="24px" />}
              aria-label="Back"
              onClick={() => setView('main')}
              color="brand.purple"
            />
            <Text as="h4" textStyle="h4Bold" color="brand.black">
              Transfer points from {potName ? `"${potName}"` : 'this pot'}
            </Text>
          </Flex>
          <Spacer />
          <IconButton
            variant="link"
            icon={<CloseCircleOutline boxSize="30px" />}
            aria-label="Close modal"
            onClick={handleClose}
          />
        </HStack>
      );
    }

    return (
      <HStack align="flex-start">
        <Flex direction="row" align="center" gap={2}>
          <YellowWarningBold boxSize="24px" />
          <Text as="h4" textStyle="h4Bold" color="brand.black">
            {`Archive ${potName ? `"${potName}"` : 'this pot'}?`}
          </Text>
        </Flex>
        <Spacer />
        <IconButton
          variant="link"
          icon={<CloseCircleOutline boxSize="30px" />}
          aria-label="Close modal"
          onClick={handleClose}
        />
      </HStack>
    );
  };

  const renderBody = () => {
    if (isLoadingPendingApprovals) {
      return (
        <Flex justify="center" py="20px">
          <Spinner color="brand.purple" />
        </Flex>
      );
    }

    if (view === 'pendingApprovals') {
      return (
        <Flex direction="column" gap="12px" position="relative" pb="70px">
          {pendingApprovals.map((item) => (
            <ApprovalCardSimplified
              key={item.id}
              requestDate={item.submittedAt}
              points={item.points}
              pointsValue={item.points ? getPointsValue(item.points) : undefined}
              isSelectable
              isProcessing={isProcessing(item)}
              approvalFailureReason={item.approvalFailureReason}
              isSelected={selected.some((s) => s.id === item.id)}
              onSelect={() => handleOnSelect(item.id)}
              requester={individualFullName({
                firstName: item.senderFirstName,
                lastName: item.senderLastName,
              } as Individual)}
              recipient={individualFullName({
                firstName: item.receiverFirstName,
                lastName: item.receiverLastName,
              } as Individual)}
              onApprove={() => openApproveConfirm([item])}
              onReject={() => openRejectConfirm([item])}
            />
          ))}
        </Flex>
      );
    }

    if (view === 'transferPoints' && liveOriginGroupWithPot) {
      return (
        <TransferPointsForm
          companyId={companyId}
          originGroupWithPot={liveOriginGroupWithPot}
          onClose={() => setView('main')}
          onSuccess={(info) => {
            refetch();
            toast({
              position: 'top-right',
              title: `${formatNumber(info.points)} points have been transferred to ${
                info.toPotName
              }`,
              description: `Both ${info.fromPotName} and ${info.toPotName} pots have been updated.`,
              status: 'success',
            });

            setView('main');
          }}
          onError={(info) => {
            toast({
              position: 'top-right',
              title: 'Failed to transfer points',
              description: `Could not transfer points from ${info.fromPotName} to ${info.toPotName}. Please try again.`,
              status: 'error',
            });
          }}
        />
      );
    }

    return (
      <ArchivePotMainView
        currentBalance={currentBalance}
        pendingCount={pendingApprovals.length}
        hasPendingApprovals={hasPendingApprovals}
        hasBalance={hasBalance}
        onViewPendingApprovals={() => setView('pendingApprovals')}
        onViewTransferPoints={() => setView('transferPoints')}
        onConfirm={handleArchivePot}
        isArchiving={isArchiving}
      />
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
        scrollBehavior="inside"
        size={view === 'pendingApprovals' ? 'xl' : 'md'}
      >
        <ModalOverlay />
        <ModalContent
          position="relative"
          sx={{ maxWidth: view === 'pendingApprovals' ? '700px !important' : '592px !important' }}
        >
          <ModalHeader pb={4}>{renderHeader()}</ModalHeader>
          <ModalBody pb={6}>
            {renderBody()}
            {view === 'pendingApprovals' && selected.length > 0 && (
              <Flex
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                justify="center"
                p="24px"
                zIndex={10}
              >
                <BulkActionBar
                  onDeselectAll={() => setSelected([])}
                  selectedCount={selected.length}
                  cta={
                    <Flex direction="row">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="bulk-approve-button"
                        onClick={() => openApproveConfirm(selected)}
                        color="brand.white"
                        _hover={{ color: 'brand.white' }}
                      >
                        <Flex direction="column" gap="5px" align="center">
                          <TickCircleOutline boxSize="20px" />
                          Approve
                        </Flex>
                      </Button>
                    </Flex>
                  }
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <ApproveRecognitionModal
        isOpen={isApproveOpen}
        isConfirming={isApproving}
        onClose={() => {
          closeApprove();
          setPendingItems([]);
        }}
        onConfirm={handleConfirmApprove}
        pendingItems={pendingItems}
      />

      <RejectRecognitionModal
        isOpen={isRejectOpen}
        onClose={() => {
          closeReject();
          setPendingItems([]);
        }}
        onConfirm={handleConfirmReject}
        pendingItems={pendingItems}
      />
    </>
  );
}

export default ArchivePotModal;
